<script setup lang="ts">

import { computed } from "vue";
import { ConditionState } from "@domain/Vehicle";
import { useSlots } from "vue";
import { IWorkOrderConfigurationDto } from "@domain/CarePlan/WorkOrders/IWorkOrderConfiguration";
import { FeatureType } from "@domain/CarePlan/WorkOrders/FeatureType";

type Props = {
    to?: string,
    icon: string,
    workOrderCondition?: IWorkOrderConfigurationDto,
    smallIcon?: boolean
    pending?: boolean
    state?: ConditionState
}

const { to = '/', smallIcon = false, pending, state, workOrderCondition } = defineProps<Props>();
const slots = useSlots();

const total = workOrderCondition == null ? 0
    : workOrderCondition.hasAnnualClaimLimit ? workOrderCondition.maximumAnnualClaimCount 
    : workOrderCondition.maximumClaimCount;
    
const used = workOrderCondition == null ? 0
    : workOrderCondition.hasAnnualClaimLimit ? workOrderCondition.annualClaimCount
    : workOrderCondition.claimCount

const max = total ?? used ?? 0;
const hasSubTitle = !!slots[ 'sub-title' ];
const isAlerted = computed( () => state == ConditionState.Due || state == ConditionState.Overdue );
const showSubTitle = computed( () => pending || hasSubTitle || used != null );

</script>

<template>

    <RouterLink 
        :to="to"
        class="border rounded-lg 2xl:rounded-xl aspect-square shadow flex flex-col items-center "
        :class="{
            'border-white/5 bg-white/5 text-white': isAlerted == false,
            'border-amber-500 bg-amber-500/75 text-amber-200': state == ConditionState.Due,
            'border-rose-500 bg-rose-500/75 text-rose-200': state == ConditionState.Overdue
        }">
        
        <!-- BADGE -->
        <div v-if="isAlerted"
            class="absolute -top-3 px-3 py-0.5 rounded text-xs uppercase font-semibold shadow-2"
            :class="{
                'bg-amber-300  text-black': state == ConditionState.Due,
                'bg-rose-400 text-black': state == ConditionState.Overdue
            }">
            Check
        </div>
        
        <!-- ICON -->
        <div class="flex items-center justify-center flex-1">
            <Icon 
                :name="icon" 
                :class="smallIcon ? 'text-3xl 2xl:text-5xl' : 'text-4xl 2xl:text-6xl'"
                class="drop-shadow-lg"  
            />
        </div>

        <!-- LABEL -->
        <div class="text-xs 2xl:text-sm text-center uppercase"
            :class="showSubTitle ? '' : 'pb-4 2xl:pb-5'">
            <slot />
        </div>

        <!-- SUB TITLE -->
        <div v-if="showSubTitle"
            class="text-xs 2xl:text-sm text-center pt-1 pb-2 2xl:pb-3"
            :class="{
                '!text-emerald-500': state != ConditionState.Due && state != ConditionState.Overdue,
                'text-white/60': hasSubTitle
            }">
            <template v-if="pending">
                <span>Pending</span>
            </template>
            <template v-else-if="hasSubTitle">
                <slot name="sub-title" />
            </template>
            <template v-else-if="used != null">
                <strong>{{ used }}</strong> of 
                <strong>{{ max }}</strong> used
            </template>
        </div>

    </RouterLink>
            
</template>